import React, { useState, useCallback, useEffect } from "react";
import { Database, Plus } from "react-feather";
import { useApi } from "../hooks/useApi";

export const SAMPLE_ORGS_POLL_MS = 10000;

const RUNS_URL = "/SuperAdmin/sample-organizations";

const statusStyles = {
  Queued: "bg-yellow-100 text-yellow-800",
  Running: "bg-blue-100 text-blue-800",
  Completed: "bg-green-100 text-green-800",
  Failed: "bg-red-100 text-red-800",
};

const isActive = (run) => run.status === "Queued" || run.status === "Running";

// API times are zone-less UTC.
const formatTime = (value) => {
  if (!value) return "—";
  const text = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

// summaryJson is a serialized string; analysis is { completed, failed, perFlow }.
export const analysisSummary = (summaryJson) => {
  if (!summaryJson) return null;
  try {
    const analysis = JSON.parse(summaryJson)?.analysis;
    if (!analysis) return null;
    const completed = Number(analysis.completed) || 0;
    const failed = Number(analysis.failed) || 0;
    return `${completed + failed} assessments (${completed} analysed, ${failed} failed)`;
  } catch {
    return null;
  }
};

// Rendered only inside the SuperAdmin page, which already redirects anyone who is not a super
// admin; the endpoints are super-admin-only on the backend as well.
const SampleOrganizations = () => {
  const apiCall = useApi();
  const [enabled, setEnabled] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [regions, setRegions] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [regionId, setRegionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadRuns = useCallback(async (silent = false) => {
    const data = await apiCall("GET", RUNS_URL, null, false, null, silent);
    setEnabled(!!data?.enabled);
    setRuns(data?.runs || []);
  }, [apiCall]);

  useEffect(() => {
    const fetchRuns = async () => {
      try {
        setLoading(true);
        await loadRuns();
      } catch (err) {
        setError("Failed to load sample organization runs");
      } finally {
        setLoading(false);
      }
    };
    fetchRuns();
  }, [loadRuns]);

  // The pickers are needed only when runs can be started.
  useEffect(() => {
    if (!enabled) return;
    apiCall("GET", "/SuperAdmin/tenants")
      .then((data) => setTenants(data || []))
      .catch(() => setError("Failed to load tenants"));
    apiCall("GET", "/Regions")
      .then((data) => setRegions(data || []))
      .catch(() => setError("Failed to load regions"));
  }, [enabled, apiCall]);

  const hasActiveRun = runs.some(isActive);

  // Poll while a run is queued or running; the interval is cleared on unmount.
  useEffect(() => {
    if (!hasActiveRun) return undefined;
    const id = setInterval(() => {
      loadRuns(true).catch(() => {});
    }, SAMPLE_ORGS_POLL_MS);
    return () => clearInterval(id);
  }, [hasActiveRun, loadRuns]);

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiCall("POST", RUNS_URL, {
        tenantId: parseInt(tenantId, 10),
        regionId: parseInt(regionId, 10),
      });
      await loadRuns();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to start the sample organization run");
    } finally {
      setSubmitting(false);
    }
  };

  const selectCls =
    "px-3 py-2 rounded-lg bg-input-bg border border-border text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50";

  return (
    <div className="mt-10">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-content-primary mb-1">
        <Database size={18} className="text-accent-cyan" />
        Sample organizations
      </h2>
      <p className="text-sm text-content-secondary mb-4">
        Creates Brightwater Mutual Insurance, a fictional insurer with deals, business units,
        vendors, analysed assessments and integration findings. Takes up to two hours. Staging and
        development only.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
          {error}
        </div>
      )}

      {loading ? null : !enabled ? (
        <div className="bg-theme-secondary border border-border rounded-xl px-6 py-4 text-sm text-content-muted">
          Sample organizations are turned off on this deployment.
        </div>
      ) : (
        <div className="bg-theme-secondary border border-border rounded-xl overflow-hidden">
          <div className="flex flex-wrap items-end gap-3 px-6 py-4 border-b border-border">
            <label className="flex flex-col gap-1 text-xs text-content-secondary">
              Tenant
              <select
                aria-label="Tenant"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                className={selectCls}
              >
                <option value="" className="bg-theme-secondary">-- Select tenant --</option>
                {tenants.map((t) => (
                  <option key={t.tenantId} value={t.tenantId} className="bg-theme-secondary">
                    {t.name} ({t.tenantId})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-content-secondary">
              Region
              <select
                aria-label="Region"
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                className={selectCls}
              >
                <option value="" className="bg-theme-secondary">-- Select region --</option>
                {regions.map((r) => (
                  <option key={r.regionId} value={r.regionId} className="bg-theme-secondary">
                    {r.regionName}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={handleCreate}
              disabled={hasActiveRun || submitting || !tenantId || !regionId}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-primary text-white text-sm rounded-lg hover:shadow-glow-cyan transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={16} />
              Create sample organization
            </button>
          </div>

          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-theme-primary/50">
                {["Created", "Organization", "Status", "Phase", "Completed"].map((h) => (
                  <th key={h} className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const summary = run.status === "Completed" ? analysisSummary(run.summaryJson) : null;
                return (
                  <React.Fragment key={run.sampleOrganizationRunId}>
                    <tr className="border-b border-border/50">
                      <td className="px-6 py-3 text-sm text-content-secondary">{formatTime(run.createdAt)}</td>
                      <td className="px-6 py-3 text-sm text-content-primary">
                        {run.organizationName || "—"}
                        {summary && <div className="text-xs text-content-muted">{summary}</div>}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            statusStyles[run.status] || "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {run.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-content-secondary">
                        {run.phase || "—"}
                        {run.phaseDetail && <div className="text-xs text-content-muted">{run.phaseDetail}</div>}
                      </td>
                      <td className="px-6 py-3 text-sm text-content-secondary">{formatTime(run.completedAt)}</td>
                    </tr>
                    {run.status === "Failed" && run.errorMessage && (
                      <tr className="border-b border-border/50">
                        <td colSpan={5} className="px-6 pb-3 text-sm text-risk-critical">
                          {run.errorMessage}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-content-muted">
                    No sample organization runs yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SampleOrganizations;
